import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Badge,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    Box,
    Button,
    Chip
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Circle as CircleIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    MarkEmailRead as MarkReadIcon,
    DeleteOutline as DeleteIcon,
    SupportAgent as SupportAgentIcon,
    ContactMail as ContactMailIcon,
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';

// Le WS passe par le backoffice_server qui proxifie vers le service ticket
const BACKOFFICE_WS = (process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:8080') + '/ws/tickets';

// Désactivé temporairement le 2026-08-23 pour isoler un bug de perte d'authentification pendant
// le test manuel de la feature de session de support -- LOCAL_URL_TICKET_API pointe vers un port
// fictif dans cet environnement de dev, donc ce WS boucle en échec permanent (bruit console). Ce
// composant ne touche par ailleurs aucun cookie/token : test d'élimination, pas un vrai suspect.
// À remettre à false une fois le vrai problème d'auth identifié.
const TICKETS_WS_DISABLED_TEMP = true;

const SERVICE_LABELS = {
    support_technique: 'Support technique',
    commercial: 'Prise de contact',
    developpement: 'Développement',
    autre: 'Autre',
};

export default function NotificationSystem() {
    const { accessToken } = useContext(TokenContext);
    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const retriesRef = useRef(0);
    const MAX_RETRIES = 10;
    const open = Boolean(anchorEl);

    const unreadCount = notifications.filter(n => !n.read).length;

    const addNotification = useCallback((notif) => {
        setNotifications(prev => [notif, ...prev].slice(0, 50)); // max 50
    }, []);

    const connectWS = useCallback(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

        const url = accessToken ? `${BACKOFFICE_WS}?token=${accessToken}` : BACKOFFICE_WS;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            retriesRef.current = 0;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if ((msg.type === 'ticket.created' || msg.event === 'ticket.created') && msg.data) {
                    const ticket = msg.data;
                    const isCommercial = ticket.service === 'commercial';
                    addNotification({
                        id: ticket.id || Date.now(),
                        type: isCommercial ? 'info' : 'warning',
                        title: isCommercial ? 'Nouvelle prise de contact' : 'Nouveau ticket support',
                        message: ticket.title || 'Un nouveau ticket a été créé',
                        service: ticket.service,
                        timestamp: new Date(ticket.created_at || Date.now()),
                        read: false,
                        priority: isCommercial ? 'medium' : (ticket.priority === 'urgent' ? 'high' : 'medium'),
                    });
                }
            } catch {}
        };

        ws.onclose = () => {
            retriesRef.current += 1;
            if (retriesRef.current <= MAX_RETRIES) {
                const delay = Math.min(1000 * 2 ** retriesRef.current, 60000);
                reconnectTimerRef.current = setTimeout(connectWS, delay);
            }
        };

        ws.onerror = () => {
            ws.close();
        };
    }, [accessToken, addNotification]);

    useEffect(() => {
        if (TICKETS_WS_DISABLED_TEMP || !accessToken) return;
        connectWS();
        return () => {
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            if (wsRef.current) wsRef.current.close();
        };
    }, [accessToken, connectWS]);

    // Heartbeat ping toutes les 30s
    useEffect(() => {
        const interval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ event: 'ping' }));
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const markAsRead = (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    };
    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };
    const deleteNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const getNotificationIcon = (type, service) => {
        const iconProps = { fontSize: 'small' };
        if (service === 'commercial') return <ContactMailIcon color="info" {...iconProps} />;
        if (service === 'support_technique') return <SupportAgentIcon color="warning" {...iconProps} />;
        switch (type) {
            case 'error': return <ErrorIcon color="error" {...iconProps} />;
            case 'warning': return <WarningIcon color="warning" {...iconProps} />;
            case 'success': return <CheckCircleIcon color="success" {...iconProps} />;
            default: return <InfoIcon color="info" {...iconProps} />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'error';
            case 'medium': return 'warning';
            default: return 'info';
        }
    };

    const formatTimestamp = (timestamp) => {
        const diff = Date.now() - new Date(timestamp).getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (minutes < 60) return `${minutes}min`;
        if (hours < 24) return `${hours}h`;
        return `${days}j`;
    };

    return (
        <Box>
            <IconButton
                onClick={handleClick}
                color="primary"
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{ sx: { width: { xs: 'calc(100vw - 32px)', sm: 380 }, maxHeight: 500, overflow: 'visible' } }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Button size="small" onClick={markAllAsRead} startIcon={<MarkReadIcon />} sx={{ color: 'primary.main' }}>
                                Tout marquer lu
                            </Button>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est lu'}
                    </Typography>
                </Box>

                <Divider />

                <List sx={{ maxHeight: 350, overflow: 'auto', p: 0 }}>
                    {notifications.length === 0 ? (
                        <ListItem>
                            <ListItemText
                                primary="Aucune notification"
                                secondary="Les nouveaux tickets apparaîtront ici en temps réel"
                                sx={{ textAlign: 'center' }}
                            />
                        </ListItem>
                    ) : notifications.map((notif) => (
                        <ListItem
                            key={notif.id}
                            sx={{
                                backgroundColor: notif.read ? 'transparent' : 'action.hover',
                                borderLeft: notif.read ? 'none' : `3px solid ${getPriorityColor(notif.priority) === 'error' ? '#f44336' : getPriorityColor(notif.priority) === 'warning' ? '#ff9800' : '#2196f3'}`,
                                '&:hover': { backgroundColor: 'action.selected' }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {getNotificationIcon(notif.type, notif.service)}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: notif.read ? 'normal' : 'bold', flex: 1 }}>
                                            {notif.title}
                                        </Typography>
                                        {!notif.read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {notif.message}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTimestamp(notif.timestamp)}
                                            </Typography>
                                            {notif.service && (
                                                <Chip
                                                    label={SERVICE_LABELS[notif.service] || notif.service}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.6rem', height: 16 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {!notif.read && (
                                        <IconButton size="small" onClick={() => markAsRead(notif.id)} title="Marquer comme lu">
                                            <MarkReadIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton size="small" onClick={() => deleteNotification(notif.id)} title="Supprimer">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>

                {notifications.length > 0 && [
                    <Divider key="divider" />,
                    <Box key="clear-all" sx={{ p: 1, textAlign: 'center' }}>
                        <Button size="small" onClick={() => setNotifications([])} sx={{ color: 'text.secondary' }}>
                            Tout effacer
                        </Button>
                    </Box>
                ]}
            </Menu>
        </Box>
    );
}
